import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import NameField from "./name_fields";

const focusableSelector = [
  "button:not([disabled])",
  "[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])'
].join(",");

const WorkShiftLogModal = ({ workShiftLog, onClose }) => {
  const dialogRef = useRef(null);
  const [formState, setFormState] = useState({
    names: workShiftLog.names.length > 0 ? workShiftLog.names : [""],
    shift_date: workShiftLog.shift_date,
    shift: workShiftLog.shift,
    location: workShiftLog.location
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusableElements = Array.from(dialogRef.current.querySelectorAll(focusableSelector));
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    window.requestAnimationFrame(() => dialogRef.current?.querySelector("input")?.focus());

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const handleNameChange = (index, value) => {
    setFormState((previousState) => ({
      ...previousState,
      names: previousState.names.map((name, nameIndex) => (nameIndex === index ? value : name))
    }));
  };

  const addNameField = () => {
    setFormState((previousState) => ({
      ...previousState,
      names: [...previousState.names, ""]
    }));
  };

  const removeNameField = (index) => {
    setFormState((previousState) => {
      if (previousState.names.length === 1) return previousState;

      return {
        ...previousState,
        names: previousState.names.filter((_, nameIndex) => nameIndex !== index)
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const emptyNameIndex = formState.names.findIndex((name) => name.trim().length === 0);
    if (emptyNameIndex >= 0) {
      setSubmitError("Enter a name for each staff member before saving.");
      dialogRef.current?.querySelectorAll("input")[emptyNameIndex]?.focus();
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    const formData = new FormData();
    formData.append("work_shift_log[names]", JSON.stringify(formState.names));
    formData.append("work_shift_log[shift_date]", formState.shift_date);
    formData.append("work_shift_log[shift]", formState.shift);

    const csrfToken = document.querySelector("[name=csrf-token]")?.content;

    try {
      const response = await fetch(`/work_shift_logs/${workShiftLog.id}`, {
        method: "PUT",
        body: formData,
        headers: csrfToken ? { "X-CSRF-Token": csrfToken } : {}
      });

      if (response.ok) {
        window.location.reload();
        return;
      }

      const data = await response.json().catch(() => ({}));
      const errorDetails = Object.values(data).flat().join(" ");
      setSubmitError(errorDetails || "The staff roster could not be saved. Try again.");
    } catch (_error) {
      setSubmitError("The staff roster could not be saved. Check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNameKeyDown = (event) => {
    if (event.key === "Enter") handleSubmit(event);
  };

  const submitLabel = workShiftLog.status === "submitted" ? "Update" : "Clock In";

  return createPortal(
    <div className="work-shift-log-modal modal is-active">
      <button
        type="button"
        className="modal-background work-shift-log-modal__backdrop"
        aria-label="Close staff roster"
        onClick={onClose}
      ></button>
      <form
        ref={dialogRef}
        id="work-shift-log-dialog"
        className="modal-card ccw-dialog work-shift-log-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="work-shift-log-dialog-title"
        aria-describedby="work-shift-log-dialog-description"
        onSubmit={handleSubmit}
      >
        <header className="modal-card-head ccw-dialog__header">
          <div className="modal-card-header">
            <h1 id="work-shift-log-dialog-title" className="modal-card-title">
              Staff roster
            </h1>
            <p id="work-shift-log-dialog-description" className="modal-card-description">
              Add everyone working this shift.
            </p>
          </div>
          <button
            type="button"
            className="work-shift-log-modal__close"
            aria-label="Close staff roster"
            onClick={onClose}
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <section className="modal-card-body ccw-dialog__body">
          <dl className="work-shift-log-modal__context" aria-label="Shift details">
            <div>
              <dt>Location</dt>
              <dd>{formState.location}</dd>
            </div>
            <div>
              <dt>Shift date</dt>
              <dd>{formState.shift_date}</dd>
            </div>
            <div>
              <dt>Shift</dt>
              <dd>{formState.shift}</dd>
            </div>
          </dl>

          <fieldset className="work-shift-log-modal__names-field">
            <legend className="label ccw-field__label">Staff working this shift</legend>
            <div className="names-container">
              {formState.names.map((name, index) => (
                <NameField
                  key={index}
                  name={name}
                  index={index}
                  onNameChange={handleNameChange}
                  onKeyDown={handleNameKeyDown}
                  onRemove={removeNameField}
                />
              ))}

              <button
                type="button"
                id="add-more-names-button"
                className="button is-secondary ccw-button ccw-button--secondary"
                onClick={addNameField}
              >
                Add more names
              </button>
            </div>
          </fieldset>

          {submitError && (
            <div className="ccw-notice ccw-notice--danger" role="alert">
              {submitError}
            </div>
          )}
        </section>

        <footer className="modal-card-foot work-shift-log-modal__actions">
          <button
            type="submit"
            className="button is-primary ccw-button ccw-button--primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving…" : submitLabel}
          </button>
          <button
            type="button"
            className="button ccw-button ccw-button--secondary"
            onClick={onClose}
          >
            Cancel
          </button>
        </footer>
      </form>
    </div>,
    document.body
  );
};

WorkShiftLogModal.propTypes = {
  workShiftLog: PropTypes.shape({
    id: PropTypes.number.isRequired,
    names: PropTypes.arrayOf(PropTypes.string).isRequired,
    shift_date: PropTypes.string.isRequired,
    shift: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired
  }).isRequired,
  onClose: PropTypes.func.isRequired
};

export default WorkShiftLogModal;
